import * as React from 'react';
import type { StageErrorSummary } from '@chess/api-insights';
import { PractiseRows, SkeletonRows, TextSkeleton, type CardProps, type FilterLink, type PractiseRow } from '@chess/api-insights/charts';
import { LOST_PIECES, type GameBlunder } from '@chess/api-model';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';

// "Moves to practise" in the ending (2026-10-08; "Ending errors", counting
// moves, before): three rows, each the games with one kind of move to
// practise — a won position let slip (whatever the result), a winning move
// missed, a piece given away — as bars in the losses' red ramp, the games
// counted at the right. Every row opens those games in the library; each
// game's review has the moves as puzzles. Scanned games of the last 30 days
// that reached an ending. Drawn from props only.

export type EndingErrorsCardProps = CardProps<{ endgame: StageErrorSummary }, {
  /** Opens the library filtered to the games with that kind of move. */
  link?: FilterLink;
}>;

const games = (n: number) => `${n} ${n === 1 ? 'game' : 'games'}`;
export const TITLE = 'Moves to practise';
const ASIDE = 'in the ending';
const PIECES: GameBlunder[] = LOST_PIECES.filter((p) => p !== 'mate').map((p) => `ending-${p}` as GameBlunder);

export const EndingErrorsCard: React.FC<EndingErrorsCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardNote>
          <TextSkeleton sample="of 59 endings · a row opens its games" />
        </CardNote>
        <SkeletonRows labels={['Won positions let slip', 'Winning moves missed', 'Pieces given away']} value="0" />
      </>
    );
  const { endgame: e, link } = props;
  const rows: PractiseRow[] = [
    {
      key: 'slip',
      label: 'Won positions let slip',
      count: e.thrownGames,
      colour: 'var(--viz-ending-slipped)',
      title: `${games(e.thrownGames)} where a move took a winning position (+2.00 or better) to under +0.50, whatever the result · opens them`,
      filter: { blunder: 'ending-won' },
    },
    {
      key: 'miss',
      label: 'Winning moves missed',
      count: e.missGames,
      colour: 'var(--viz-ending-missed)',
      title: `${games(e.missGames)} with a chance not taken in the ending · opens them`,
      filter: { blunder: 'ending-miss' },
    },
    {
      key: 'piece',
      label: 'Pieces given away',
      count: e.lostAnyGames,
      colour: 'var(--viz-loss)',
      title: `${games(e.lostAnyGames)} with a blunder that gave a piece away in the ending · opens them`,
      filter: { blunder: PIECES },
    },
  ];
  const any = rows.some((r) => r.count > 0);
  const note = e.scanned ? `of ${e.scanned === 1 ? '1 ending' : `${e.scanned} endings`} · a row opens its games; each review has the puzzle` : undefined;
  let body: React.ReactNode;
  if (!e.scanned) body = <CardEmpty>Scan games to find your ending moves to practise.</CardEmpty>;
  else if (!any) body = <CardEmpty>Nothing to practise in the last 30 days' endings.</CardEmpty>;
  else body = <PractiseRows rows={rows} link={link} />;
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      {note && <CardNote>{note}</CardNote>}
      {body}
    </>
  );
};
